import { useState, useContext, useEffect, forwardRef } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QuestionType from "./components/QuestionType";
import MatchingCriteria from "./components/MatchingCriteria";
import MatchingAnswer from "./components/MatchingAnswer";
import Choices from "./components/Choices";
import AnswerMap from "./components/AnswerMap";
import RelatedQuestion from "./components/RelatedQuestion";
import ConfirmDelete from "./components/ConfirmDelete";
import ProfileQuestionContext from "../../../../contexts/ProfileQuestionContext";
import request from "../../../../utilities/requests";
import { toast } from "../../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const questionTypes = [
  { label: "Yes/No", value: "yesno" },
  { label: "Choice", value: "choice" },
  { label: "Multiple Choice", value: "multipleChoice" },
  { label: "Range", value: "range" },
  { label: "Related", value: "related" },
  { label: "Text", value: "text" },
];
const matchingCriteria = ["same", "different", "multiple", "range", "none"];
const priorities = [1, 2, 3];
const rangeTypes = [
  { label: "Less than or equal to answer", value: "<=" },
  { label: "Equal to answer", value: "==" },
  { label: "Greater than or equal to answer", value: ">=" },
];

const questionTypeDescriptions = {
  yesno: "Users have a yes or no answer choice",
  choice: "Users can select one answer from a number of choices",
  multipleChoice: "Users can select multiple answers from a number of choices",
  range: "Users select an answer based on a number range (e.g. 1 - 10)",
  text: "User can input free text to answer the question",
};

const matchingDescriptions = {
  same: "Matches users with identical answers.",
  different: "A different answer or answers are considered to be matching",
  range: "Matches users within a numerical range.",
};

// const matchPriorityDescriptions = {
//   ""
// }

const QuestionDialog = ({ refresh }) => {
  const {
    questionDialogOpen,
    setQuestionDialogOpen,
    org,
    editQuestionData,
    setEditQuestionData,
  } = useContext(ProfileQuestionContext);
  const [error, setError] = useState("You are wrong");
  const [questionData, setQuestionData] = useState({
    question: "",
    systemLabel: "",
    description: "",
    questionType: "",
    active: true,
    matchingCriteria: "",
    // matchingPriority: "",
    matchingAnswer: "",
    relatedQuestion: null,
    lowerRangeLabel: "",
    lowerRangeValue: "",
    upperRangeLabel: "",
    upperRangeValue: "",
    choices: [],
    weight: 5,
  });
  const [baseQuestion, setBaseQuestion] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (editQuestionData) {
      setQuestionData(editQuestionData);
      if (editQuestionData.baseQuestion) {
        setBaseQuestion(true);
      } else {
        setBaseQuestion(false);
      }
    }
  }, [editQuestionData]);

  useEffect(() => {
    const validateQuestionData = () => {
      let errorMessage = "";

      if (
        questionData.questionType === "yesno" &&
        !["same", "different", "none"].includes(questionData.matchingCriteria)
      ) {
        errorMessage =
          "Yes/No questions can only have a matching criteria of 'same' or 'none'";
      } else if (
        questionData.questionType === "choice" &&
        !["same", "different", "none"].includes(questionData.matchingCriteria)
      ) {
        errorMessage =
          "Choice questions can only have a matching criteria of 'same', or 'none'";
      } else if (
        questionData.questionType === "multipleChoice" &&
        !["same", "multiple", "none"].includes(questionData.matchingCriteria)
      ) {
        errorMessage =
          "Multiple choice questions can only have a matching criteria of 'same', 'multiple', or 'none'";
      }

      setError(errorMessage); // Update the error state
    };

    validateQuestionData();
  }, [questionData.questionType, questionData.matchingCriteria]); // Re-run

  const handleChange = (field, value) => {
    setQuestionData((prev) => {
      if (field === "questionType") {
        // Reset choices and matchingAnswer when questionType changes.
        // Text questions don't participate in matching, so force "none".
        const next = {
          ...prev,
          [field]: value,
          choices: value === "choice" || value === "multipleChoice" ? [] : [],
          matchingAnswer: "",
          relatedQuestion: null,
        };
        if (value === "text") next.matchingCriteria = "none";
        return next;
      }
      if (error) setError("");
      return { ...prev, [field]: value };
    });
  };

  const handleMatchingAnswerChange = (value) => {
    // For multipleChoice, convert selected values into a comma-separated string
    if (Array.isArray(value)) {
      value = value.join(",");
    }
    setQuestionData((prev) => ({ ...prev, matchingAnswer: value }));
    if (error) setError("");
  };

  const handleRangeChange = (field, value) => {
    setQuestionData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const toCamelCase = (str) => {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
  };

  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...questionData.choices];
    const camelCaseValue = toCamelCase(value);
    updatedChoices[index] = { label: value, value: camelCaseValue };
    setQuestionData((prev) => ({ ...prev, choices: updatedChoices }));
    if (error) setError("");
  };

  const addChoice = () => {
    setQuestionData((prev) => ({
      ...prev,
      choices: [...prev.choices, { label: "", value: "" }],
    }));
    if (error) setError("");
  };

  const removeChoice = (index) => {
    const updatedChoices = questionData.choices.filter((_, i) => i !== index);
    setQuestionData((prev) => ({ ...prev, choices: updatedChoices }));
  };

  const validateChoices = () => {
    for (let choice of questionData.choices) {
      if (choice?.value === "") {
        return false;
      }
    }

    return true;
  };

  const validateOnSave = () => {
    if (error) {
      return false;
    }
    let err;
    if (!questionData.question) {
      err = "You must enter some question text.";
    } else if (!questionData.questionType) {
      err = "You must select a question type.";
    } else if (
      questionData.questionType !== "text" &&
      !questionData.matchingCriteria
    ) {
      err = "You must select matching criteria for the question.";
    } else if (
      ["choice", "multiple"].includes(questionData.questionType) &&
      questionData.choices.length <= 1
    ) {
      err = "You must enter more than one answer choices";
    } else if (
      ["choice", "multiple"].includes(questionData.questionType) &&
      questionData.choices.length > 1 &&
      !validateChoices()
    ) {
      err = "Please ensure all of your answer choices have values";
    }
    // else if (
    //   questionData.questionType === "choice" &&
    //   questionData.matchingCriteria === "different" &&
    //   !questionData.matchingAnswer
    // ) {
    //   err = "You must select a matching answer.";
    // }
    else if (
      questionData.questionType === "multipleChoice" &&
      questionData.matchingCriteria === "multiple" &&
      !questionData.matchingAnswer
    ) {
      err = "You must select at least one matching answer.";
    } else if (
      questionData.questionType === "range" &&
      !questionData.lowerRangeValue
    ) {
      err = "You must select a lower range value";
    }
    // else if (
    //   questionData.questionType === "range" &&
    //   !questionData.lowerRangeLabel
    // ) {
    //   err = "You must enter a label for the lower range";
    // }
    else if (
      questionData.questionType === "range" &&
      !questionData.upperRangeValue
    ) {
      err = "You must select a upper range value";
    }
    // else if (
    //   questionData.questionType === "range" &&
    //   !questionData.upperRangeLabel
    // ) {
    //   err = "You must enter a label for the upper range";
    // }
    // else if (!questionData.matchingPriority) {
    //   err = "You must select a matching priority";
    // }

    if (err) {
      setError(err);
      return false;
    }

    console.log("SAVING!");
    return true;
  };

  const handleSave = async () => {
    const canSave = validateOnSave();
    if (!canSave) return;
    try {
      let body;
      let url;
      let msg;
      if (editQuestionData) {
        body = {
          questionId: editQuestionData._id,
          update: questionData,
        };
        // if (questionData.questionType !== 'related' ) {
        //   delete body.update.relatedQuestion;
        // }
        url = "profilequestions/update";
        msg = "Question updated successfully";
      } else {
        body = {
          orgId: org,
          question: questionData,
        };
        // if (questionData.questionType !== 'related' ) {
        //   delete body.question.relatedQuestion;
        // }
        url = "profilequestions/create";
        msg = "Question created successfully";
      }

      const result = await request(url, "post", body);
      if (result.success) {
        toast.success(msg);
        handleClose();
      }
    } catch (e) {
      toast.error(`Error saving question. Error: ${e.message}`);
      return;
    }
  };

  const handleClose = () => {
    refresh();
    setQuestionDialogOpen(false);
    resetForm();
    setEditQuestionData(null);
    setBaseQuestion(false);
    if (confirmDelete) setConfirmDelete(false);
  };

  const resetForm = () => {
    setQuestionData({
      question: "",
      systemLabel: "",
      description: "",
      questionType: "",
      active: true,
      matchingCriteria: "",
      // matchingPriority: "",
      matchingAnswer: "",
      relatedQuestion: null,
      lowerRangeLabel: "",
      lowerRangeValue: "",
      upperRangeLabel: "",
      upperRangeValue: "",
      choices: [],
      weight: 5,
    });
  };

  const handleDelete = async () => {
    const body = { questionId: editQuestionData._id };
    const result = await request("profilequestions/delete", "post", body);
    if (result.success && result.deleted === false) {
      setConfirmDelete(false);
      toast.error(result.message);
      return;
    } else if (result.success) {
      toast.success("Question deleted successfully!");
      handleClose();
    }
  };

  return (
    <Dialog
      fullScreen
      open={questionDialogOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {questionData._id ? "Edit Question" : "New Question"}
          </Typography>

          <Button autoFocus color="inherit" onClick={handleSave}>
            Save
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ padding: 4 }}>
        <Grid container spacing={3}>
          {/* Error Message */}
          {error && (
            <Grid
              item
              xs={12}
              style={{
                backgroundColor: "pink",
                padding: 16,
                marginLeft: 20,
                marginTop: 10,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" color="#a60000">
                {error}
              </Typography>
            </Grid>
          )}
          {baseQuestion ? (
            <Grid item xs={12}>
              <Typography
                align="center"
                style={{ fontWeight: "bold", fontSize: 18 }}
              >
                This is a base question in the system. Only the weight can be
                edited.
              </Typography>
            </Grid>
          ) : (
            <></>
          )}
          {/* System Label */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="System Label"
              value={
                questionData.systemLabel
                  ? questionData.systemLabel
                  : questionData.question
              }
              onChange={(e) => handleChange("systemLabel", e.target.value)}
              disabled={baseQuestion}
            />
            <Typography variant="caption" color="textSecondary">
              Help denote similar questions that might be used for different
              groups (e.g. pronouns). Defaults to question text. The user will
              not see this.
            </Typography>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={questionData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
            />
            <Typography variant="caption" color="textSecondary">
              Any helpful information on the question. The user will see this if
              there is text entered.
            </Typography>
          </Grid>

          {/* Question Text */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Question Text"
              value={questionData.question}
              onChange={(e) => handleChange("question", e.target.value)}
              disabled={baseQuestion}
            />
            <Typography variant="caption" color="textSecondary">
              The question the user will see in their app
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="question-weight-label">
                Question Weight
              </InputLabel>
              <Select
                labelId="question-weight-label"
                label="Question Weight"
                value={questionData.weight || 5}
                onChange={(e) => handleChange("weight", Number(e.target.value))}
              >
                {[...Array(10)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary">
              The weight of the question towards calculating the match score (1
              - 10)
            </Typography>
          </Grid>
          <Grid item xs={6} />

          <QuestionType
            questionData={questionData}
            handleChange={handleChange}
            questionTypes={questionTypes}
            questionTypeDescriptions={questionTypeDescriptions}
            baseQuestion={baseQuestion}
          />

          {/* Matching Criteria */}
          <MatchingCriteria
            questionData={questionData}
            handleChange={handleChange}
            matchingCriteria={matchingCriteria}
            matchingDescriptions={matchingDescriptions}
            baseQuestion={baseQuestion}
          />

          <MatchingAnswer
            questionData={questionData}
            handleRangeChange={handleRangeChange}
            handleMatchingAnswerChange={handleMatchingAnswerChange}
            baseQuestion={baseQuestion}
          />

          <RelatedQuestion
            questionData={questionData}
            handleChange={handleChange}
            handleMatchingAnswerChange={handleMatchingAnswerChange}
            baseQuestion={baseQuestion}
          />

          {/* Choices */}
          <Choices
            questionData={questionData}
            handleChoiceChange={handleChoiceChange}
            removeChoice={removeChoice}
            addChoice={addChoice}
            baseQuestion={baseQuestion}
          />

          <AnswerMap
            questionData={questionData}
            handleChange={handleChange}
            baseQuestion={baseQuestion}
          />

          {/* <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Matching Priority</InputLabel>
              <Select
                value={questionData.matchingPriority || ""}
                onChange={(e) =>
                  handleChange("matchingPriority", e.target.value)
                }
              >
                <MenuItem value="">Select Matching Priority</MenuItem>
                {priorities.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="textSecondary">
                1 - Deal breaker, 2 - Nice to have, 3 - Doesn't matter
              </Typography>
            </FormControl>
          </Grid> */}

          {/* Active Switch */}
          {/* <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={questionData.active}
                  onChange={(e) => handleChange("active", e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid> */}

          <Grid item xs={12} style={{ marginTop: 50 }}>
            <Button
              variant="contained"
              style={{ width: "100%" }}
              onClick={handleSave}
            >
              save
            </Button>
          </Grid>

          {editQuestionData && editQuestionData?.canDelete ? (
            <Grid
              item
              xs={12}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Button
                variant="outlined"
                style={{ color: "red", border: "1px solid red", width: "100%" }}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Box>
      <ConfirmDelete
        confirmDelete={confirmDelete}
        handleDelete={handleDelete}
        close={() => setConfirmDelete(false)}
      />
    </Dialog>
  );
};

export default QuestionDialog;
